import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  getSupabaseProductsByMaxPrice,
  getSupabaseProductsByMaxPriceAndCategory,
  getAllSubCategories,
  slugify,
} from "../lib/productService";
import { useCartWishlist } from "../contexts/CartWishlistContext";
import "./PriceRangePage.css";

const PATH_TO_PRICE = {
  "/under99": 99,
  "/under499": 499,
  "/under999": 999,
  "/under1499": 1499,
};

const PRICE_TITLES = {
  99: { title: "Under ₹99", subtitle: "Affordable everyday essentials that add subtle charm." },
  499: { title: "Under ₹499", subtitle: "Elegant picks that balance style and budget." },
  999: { title: "Under ₹999", subtitle: "Statement pieces for festive and special occasions." },
  1499: { title: "Under ₹1499", subtitle: "Premium luxury designs at accessible prices." },
};

// productService doesn't export resolveImageUrl - use inline
function getImgUrl(p) {
  if (!p?.images?.[0]) return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23f3f3f3' width='200' height='200'/%3E%3Ctext fill='%23999' font-size='12' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";
  const u = p.images[0];
  if (typeof u === "string" && (u.startsWith("http") || u.startsWith("data:"))) return u;
  return u;
}

// Base path without category (e.g. /under99)
function getBasePath(pathname) {
  const match = pathname.match(/^(\/under\d+)/);
  return match ? match[1] : pathname;
}

export default function PriceRangePage() {
  const { pathname } = useLocation();
  const { category: categorySlug } = useParams();
  const basePath = getBasePath(pathname);
  const maxPrice = PATH_TO_PRICE[basePath];
  const config = maxPrice ? PRICE_TITLES[maxPrice] : null;

  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart, toggleWish, isWished } = useCartWishlist();

  // Resolve category name from slug (e.g. "earrings" -> "Earrings")
  const categoryName = useMemo(() => {
    if (!categorySlug) return null;
    const slug = categorySlug.toLowerCase();
    return subCategories.find((c) => slugify(c) === slug) || null;
  }, [categorySlug, subCategories]);

  // Load sub-categories from DB + unique from products
  useEffect(() => {
    if (!maxPrice) return;
    Promise.all([
      getAllSubCategories(),
      getSupabaseProductsByMaxPrice(maxPrice, 200),
    ]).then(([dbCats, prods]) => {
      const productCats = [...new Set(prods.map((p) => p.category).filter(Boolean))];
      const merged = [...new Set([...dbCats, ...productCats])].sort();
      setSubCategories(merged);
      setAllProducts(prods);
    });
  }, [maxPrice]);

  // Load products (all or by category)
  useEffect(() => {
    if (!maxPrice) return;
    setLoading(true);
    getSupabaseProductsByMaxPriceAndCategory(maxPrice, categoryName)
      .then(setProducts)
      .finally(() => setLoading(false));
  }, [maxPrice, categoryName]);

  if (!config) {
    return (
      <div className="priceRangePage">
        <div className="priceRangeInner">
          <h1>Page not found</h1>
          <p>This price range is not available.</p>
        </div>
      </div>
    );
  }

  const { title, subtitle } = config;

  return (
    <div className="priceRangePage">
      <div className="priceRangeInner">
        <h1 className="priceRangeTitle">{title}</h1>
        <p className="priceRangeSub">{subtitle}</p>

        {subCategories.length > 0 && (
          <div className="priceRangeFilters">
            <Link
              to={basePath}
              className={`priceRangeFilterBtn ${!categorySlug ? "active" : ""}`}
            >
              All
            </Link>
            {subCategories.map((cat) => {
              const slug = slugify(cat);
              const count = allProducts.filter((p) => p.category === cat).length;
              if (count === 0) return null;
              return (
                <Link
                  key={cat}
                  to={`${basePath}/${slug}`}
                  className={`priceRangeFilterBtn ${categorySlug === slug ? "active" : ""}`}
                >
                  {cat}
                </Link>
              );
            })}
          </div>
        )}

        {loading ? (
          <p className="priceRangeLoading">Loading products...</p>
        ) : products.length === 0 ? (
          <p className="priceRangeEmpty">No products in this price range yet. Add products in the Dashboard.</p>
        ) : (
          <div className="priceRangeGrid">
            {products.map((p) => {
              const item = {
                id: p.id,
                name: p.title,
                price: p.offerPrice || p.price || 0,
                img: getImgUrl(p),
                link: `/product/${p.slug || p.id}`,
              };
              const wished = isWished(p.id);
              return (
                <div key={p.id} className="priceRangeCard">
                  <Link to={item.link} className="priceRangeCardLink">
                    <div className="priceRangeCardImgWrap">
                      <img
                        className="priceRangeCardImg"
                        src={item.img}
                        alt={p.title}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23f3f3f3' width='200' height='200'/%3E%3Ctext fill='%23999' font-size='12' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";
                        }}
                      />
                    </div>
                    <div className="priceRangeCardInfo">
                      <span className="priceRangeName">{p.title}</span>
                      <span className="priceRangePrice">₹{Math.round(item.price)}</span>
                    </div>
                  </Link>
                  <div className="priceRangeCardActions">
                    <button
                      type="button"
                      className="priceRangeAddCart"
                      onClick={() => addToCart(item)}
                    >
                      Add to Cart
                    </button>
                    <button
                      type="button"
                      className={`priceRangeWish ${wished ? "wished" : ""}`}
                      onClick={() => toggleWish(item)}
                      aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
                    >
                      {wished ? "♥" : "♡"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
